import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaSummaryBarComponent } from '../../../../shared/components/quota-summary-bar/quota-summary-bar';
import { MEAT_TYPE_TABS } from '../../../../core/constants/app.constants';
import { QuotaService } from '../../../quotas/services/quota.service';
import { AuthService } from '../../../../core/services/auth.service';
import { QuotaAssignment } from '../../../quotas/models/assignment.interface';
import { KgToTonsPipe } from '../../../../shared/pipes/kg-to-tons.pipe';
import { AddVolumeDialogComponent } from '../../components/add-volume-dialog/add-volume-dialog.component';
import { RevokeQuotaDialogComponent } from '../../components/revoke-quota-dialog/revoke-quota-dialog.component';

@Component({
  selector: 'app-current-suppliers',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatMenuModule,
    MatDividerModule,
    MatSnackBarModule,
    QuotaSummaryBarComponent,
    KgToTonsPipe,
    TranslateModule
  ],
  templateUrl: './current-suppliers.html',
  styleUrl: './current-suppliers.css',
})
export class CurrentSuppliers implements OnInit {
  private quotaService = inject(QuotaService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);

  loading = signal(false);
  error = signal<string | null>(null);
  activeTab = signal('all');
  assignments = signal<QuotaAssignment[]>([]);
  tabCounts = signal<Record<string, number>>({});

  tabs = MEAT_TYPE_TABS;

  // Проверка роли - quota_manager может только просматривать
  isManager = computed(() => {
    return this.authService.hasRole('quota_viewer') || (this.authService.hasRole('quota_manager') && !this.authService.hasRole('quota_operator'));
  });

  displayedColumns = computed(() => {
    const columns = [
      'number',
      'year',
      'stage',
      'companyName',
      'requestedVolume',
      'allocatedVolume',
      'remainderVolume',
      'meatType',
      'status'
    ];

    // Добавляем колонку действий только если НЕ менеджер
    if (!this.isManager()) {
      columns.push('actions');
    }

    return columns;
  });

  ngOnInit(): void {
    this.loadTabCounts();
    this.loadData();
  }

  loadTabCounts(): void {
    this.quotaService.getAllAssignments({ page: 1, size: 1000 }).subscribe({
      next: (response) => {
        if (response.success) {
          const assignments = response.res.content || [];
          const counts: Record<string, number> = { 'all': assignments.length };

          // Подсчитываем по видам мяса (преобразуем alias в нижний регистр)
          assignments.forEach((assignment: QuotaAssignment) => {
            const meatTypeAlias = assignment.application.meatType.alias.toLowerCase();
            counts[meatTypeAlias] = (counts[meatTypeAlias] || 0) + 1;
          });

          // Подсчитываем изъятые (REVOKED)
          const revokedCount = assignments.filter((a: QuotaAssignment) => a.status === 'REVOKED').length;
          counts['withdrawn'] = revokedCount;

          this.tabCounts.set(counts);
        }
      },
      error: (err) => {
        console.error('Error loading tab counts:', err);
      }
    });
  }

  loadData(): void {
    this.loading.set(true);
    this.error.set(null);

    this.quotaService.getAllAssignments({ page: 1, size: 1000 }).subscribe({
      next: (response) => {
        if (response.success) {
          let assignments = response.res.content || [];

          // Фильтруем по активному табу
          const activeTab = this.activeTab();
          if (activeTab !== 'all') {
            if (activeTab === 'withdrawn') {
              // Фильтруем изъятые
              assignments = assignments.filter(a => a.status === 'REVOKED');
            } else {
              // Фильтруем по виду мяса
              assignments = assignments.filter(a =>
                a.application.meatType.alias.toLowerCase() === activeTab
              );
            }
          }

          this.assignments.set(assignments);
        } else {
          this.error.set(response.msg || this.translate.instant('currentSuppliers.errors.loadingData'));
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading assignments:', err);
        this.error.set(this.translate.instant('currentSuppliers.errors.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  setActiveTab(tab: string): void {
    this.activeTab.set(tab);
    this.loadData();
  }

  getActiveTabLabel(): string {
    const tab = this.tabs.find(t => t.value === this.activeTab());
    return tab ? this.translate.instant(tab.labelKey) : '';
  }

  getTabCount(tabValue: string): number {
    return this.tabCounts()[tabValue] || 0;
  }

  getStageLabel(stage: string): string {
    const stageKeys: Record<string, string> = {
      'STAGE_1': 'currentSuppliers.stages.stage1',
      'STAGE_2': 'currentSuppliers.stages.stage2',
      'STAGE_3': 'currentSuppliers.stages.stage3',
      'STAGE_FOR_NEW_SUPPLIERS': 'currentSuppliers.stages.newSuppliers'
    };
    return stageKeys[stage] ? this.translate.instant(stageKeys[stage]) : stage;
  }

  getStatusLabel(status: string): string {
    const statusKeys: Record<string, string> = {
      'ACTIVE': 'currentSuppliers.statuses.active',
      'REVOKED': 'currentSuppliers.statuses.revoked',
      'COMPLETED': 'currentSuppliers.statuses.completed'
    };
    return statusKeys[status] ? this.translate.instant(statusKeys[status]) : status;
  }

  addVolume(assignment: QuotaAssignment): void {
    const dialogRef = this.dialog.open(AddVolumeDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { assignment }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Перезагрузить данные после успешного добавления объема
        this.loadData();
        this.loadTabCounts();
      }
    });
  }

  revokeQuota(assignment: QuotaAssignment): void {
    const dialogRef = this.dialog.open(RevokeQuotaDialogComponent, {
      width: '600px',
      disableClose: true,
      data: { assignment }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Перезагрузить данные после успешного изъятия квоты
        this.loadData();
        this.loadTabCounts();
      }
    });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
